import React from 'react';
import { founderDashboardAPI } from '../../services/api';
import { useFeatureAnalytics } from '../../hooks/useFeatureAnalytics';
import FilterBar from '../../components/feature-analytics/FilterBar';
import KPICard from '../../components/founder-dashboard/KPICard';
import TrendChart from '../../components/founder-dashboard/TrendChart';
import DistributionChart from '../../components/founder-dashboard/DistributionChart';
import DataTable from '../../components/founder-dashboard/DataTable';
import RefreshIndicator from '../../components/founder-dashboard/RefreshIndicator';
import { HeartIcon } from '@heroicons/react/24/outline';

export default function PrayerAnalyticsPage() {
  const {
    data, loading, error, showError, lastUpdated, fetchData,
    organizationId, campusId, groupId, handleFilterChange
  } = useFeatureAnalytics(founderDashboardAPI.getPrayerAnalytics);

  if (showError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-800 mb-4">{error}</p>
        <button onClick={fetchData} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
          Retry
        </button>
      </div>
    );
  }

  const { kpis = {}, trends = [], distribution = {}, table = [] } = data || {};

  const columns = [
    { key: 'name', header: 'Group Name', sortable: true },
    { key: 'organizationName', header: 'Organization', sortable: true },
    {
      key: 'prayers30d',
      header: 'Prayers (30d)',
      sortable: true,
      render: (value) => value?.toLocaleString() || '0'
    },
    {
      key: 'uniqueUsers',
      header: 'Unique Users',
      sortable: true,
      render: (value) => value?.toLocaleString() || '0'
    },
    {
      key: 'answerRate',
      header: 'Answer Rate',
      sortable: true,
      render: (value) => typeof value === 'number' ? `${(value * 100).toFixed(1)}%` : 'N/A'
    }
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <HeartIcon className="h-6 w-6 text-rose-600" />
          <div>
            <h2 className="text-xl font-semibold text-slate-800">Prayer Analytics</h2>
            <p className="text-slate-600 text-sm">Prayer feature adoption, usage, and effectiveness</p>
          </div>
        </div>
        <RefreshIndicator lastUpdated={lastUpdated} onRefresh={fetchData} loading={loading} />
      </div>

      <FilterBar
        organizationId={organizationId}
        campusId={campusId}
        groupId={groupId}
        onFilterChange={handleFilterChange}
      />

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KPICard
          title="Adoption Rate"
          value={kpis.adoption || 0}
          format="percent"
          loading={loading && !data}
        />
        <KPICard
          title="Prayers per User"
          value={kpis.prayersPerUser || 0}
          format="decimal"
          loading={loading && !data}
        />
        <KPICard
          title="Answer Rate"
          value={kpis.answerRate || 0}
          format="percent"
          loading={loading && !data}
        />
        <KPICard
          title="Median Days to Answer"
          value={kpis.medianDaysToAnswer || 'N/A'}
          subtitle="days"
          loading={loading && !data}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <h3 className="text-lg font-medium text-slate-800 mb-4">30-Day Prayer Trends</h3>
          <TrendChart
            data={trends}
            lines={[
              { key: 'public', name: 'Public Prayers', color: '#10B981' },
              { key: 'private', name: 'Private Prayers', color: '#F59E0B' }
            ]}
            type="area"
            height={250}
          />
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <h3 className="text-lg font-medium text-slate-800 mb-4">Privacy Distribution</h3>
          <DistributionChart
            data={distribution.privacy || {}}
            showPercentage={true}
            height={250}
          />
        </div>
      </div>

      {/* Category Distribution */}
      <div className="bg-white rounded-lg border border-slate-200 p-4 mb-8">
        <h3 className="text-lg font-medium text-slate-800 mb-4">Prayer Categories</h3>
        <DistributionChart
          data={distribution.category || {}}
          showPercentage={true}
          height={200}
        />
      </div>

      {/* Top Groups Table */}
      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <h3 className="text-lg font-medium text-slate-800 mb-4">Top Prayer Groups</h3>
        <DataTable
          data={table}
          columns={columns}
          loading={loading && !data}
          emptyMessage="No prayer activity found"
        />
      </div>
    </div>
  );
}
